import * as THREE from "three";
import { Evaluator, Brush, SUBTRACTION, ADDITION } from "three-bvh-csg";
import { getLocalPoints, getWorldPoints, pointInPolygon } from "./shapes";
import { levelColor } from "../constants/defaults";

const EPS = 0.02;
const evaluator = new Evaluator();
evaluator.useGroups = false;

export function resolveConnector(connector, settings) {
  const o = connector.override || {};
  const shape = o.shape ?? settings.pegShape;
  const tolerance = num(o.tolerance, settings.tolerance);
  const femaleSize = num(o.femaleSize, settings.pegSize);
  const maleHeight = num(o.maleHeight, settings.maleHeight);
  const femaleExtra = num(o.femaleExtra, settings.femaleExtra);
  const chamfer = num(o.chamfer, settings.chamfer);
  const maleSize = Math.max(0.2, femaleSize - 2 * tolerance);
  const femaleDepth = maleHeight + femaleExtra;
  return { shape, tolerance, femaleSize, maleSize, maleHeight, femaleExtra, femaleDepth, chamfer };
}
const num = (v, d) => (v === undefined || v === null || v === "" || isNaN(v) ? d : Number(v));
const DEG = (d) => ((d || 0) * Math.PI) / 180;

// Bottom z of each level (contiguous stack over the levels present).
export function computeLevels(layers, baseTop = 0) {
  const present = [...new Set(layers.map((s) => s.level))].sort((a, b) => a - b);
  const thick = new Map();
  present.forEach((l) => {
    const ts = layers.filter((s) => s.level === l).map((s) => s.thickness || 2);
    thick.set(l, ts.length ? Math.max(...ts) : 2);
  });
  const bottom = new Map();
  let z = baseTop;
  present.forEach((l) => { bottom.set(l, z); z += thick.get(l); });
  return { present, bottom, thick, topZ: z, idxOf: new Map(present.map((l, i) => [l, i])) };
}

function shapeFromPlate(plate) {
  const pts = getLocalPoints(plate);
  const shape = new THREE.Shape();
  shape.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) shape.lineTo(pts[i].x, pts[i].y);
  shape.closePath();
  return shape;
}

function csg(aGeo, bGeos, op) {
  let brush = new Brush(aGeo);
  brush.updateMatrixWorld();
  for (const bg of bGeos) {
    const b = new Brush(bg);
    b.updateMatrixWorld();
    brush = evaluator.evaluate(brush, b, op);
  }
  return brush.geometry;
}

function subtractFrom(geo, holes) {
  if (!holes || !holes.length) return geo;
  try { return csg(geo, holes, SUBTRACTION); } catch (e) { return geo; }
}

// Plate with the edge finish applied to the TOP face only (flat bottom).
export function plateGeometry(plate, zBottom) {
  const ec = Math.max(0, plate.edgeChamfer || 0);
  const s = shapeFromPlate(plate);
  let geo;
  if (ec > 0 && plate.thickness > 0.25) {
    const cV = Math.min(ec, plate.thickness * 0.9);
    const segs = plate.edgeType === "fillet" ? 4 : 1;
    const depth = Math.max(0.01, plate.thickness - cV);
    geo = new THREE.ExtrudeGeometry(s, { depth, bevelEnabled: true, bevelThickness: cV, bevelSize: ec, bevelSegments: segs, steps: 1 });
    geo.computeBoundingBox();
    geo.translate(0, 0, -geo.boundingBox.min.z); // z: 0..(depth+2cV); bottom bevel occupies [0,cV]
    try {
      const bb = geo.boundingBox;
      const sx = (bb.max.x - bb.min.x) + 20, sy = (bb.max.y - bb.min.y) + 20;
      const slab = new THREE.BoxGeometry(sx * 2, sy * 2, cV + 1);
      slab.translate((bb.max.x + bb.min.x) / 2, (bb.max.y + bb.min.y) / 2, (cV - 1) / 2); // z in [-1, cV]
      geo = csg(geo, [slab], SUBTRACTION);
      geo.computeBoundingBox();
      geo.translate(0, 0, -geo.boundingBox.min.z);
    } catch (e) { /* fall back to double-beveled */ }
  } else {
    geo = new THREE.ExtrudeGeometry(s, { depth: plate.thickness, bevelEnabled: false, steps: 1 });
  }
  geo.rotateZ(DEG(plate.rotation));
  geo.translate(plate.x, plate.y, zBottom);
  return geo;
}

// Chamfered square/diamond prism, z from 0..height, centered on x/y.
export function chamferedPrism(size, height, chamfer, diamond) {
  const c = Math.max(0, Math.min(chamfer, size * 0.3, height * 0.4));
  const h = size / 2;
  const s = new THREE.Shape();
  s.moveTo(-h, -h); s.lineTo(h, -h); s.lineTo(h, h); s.lineTo(-h, h); s.lineTo(-h, -h);
  const opts = c > 0
    ? { depth: Math.max(0.01, height - 2 * c), bevelEnabled: true, bevelThickness: c, bevelSize: c, bevelSegments: 1, steps: 1 }
    : { depth: height, bevelEnabled: false, steps: 1 };
  const geo = new THREE.ExtrudeGeometry(s, opts);
  if (diamond) geo.rotateZ(Math.PI / 4);
  geo.computeBoundingBox();
  geo.translate(0, 0, -geo.boundingBox.min.z);
  return geo;
}

export function baseMatrix(base) {
  const t = base.transform || { x: 0, y: 0, z: 0, rx: 0, ry: 0, rz: 0 };
  const m = new THREE.Matrix4();
  m.makeRotationFromEuler(new THREE.Euler(DEG(t.rx), DEG(t.ry), DEG(t.rz), "XYZ"));
  m.setPosition(t.x || 0, t.y || 0, t.z || 0);
  return m;
}
export function transformedBaseGeoms(base) {
  if (!base) return [];
  const m = baseMatrix(base);
  return base.geometries.map((g) => g.clone().applyMatrix4(m));
}
export function baseTopZ(base) {
  if (!base) return 0;
  const box = new THREE.Box3();
  transformedBaseGeoms(base).forEach((g) => { g.computeBoundingBox(); box.union(g.boundingBox); });
  return box.max.z;
}
export function buildBaseFinal(base, baseHoles) {
  return transformedBaseGeoms(base).map((g) => subtractFrom(g, baseHoles));
}

// Core build: shapes grouped by level; holes cut into the shapes on the level below.
export function buildAll(layers, settings, baseTop, hasBase) {
  const { present, bottom, topZ, idxOf } = computeLevels(layers, baseTop);
  const byLevel = new Map(present.map((l) => [l, layers.filter((s) => s.level === l)]));
  const holesForShape = {};
  const baseHoles = [];
  const entries = layers.map((shape) => ({ shape, zB: bottom.get(shape.level), pegGeoms: [] }));
  const byId = new Map(entries.map((e) => [e.shape.id, e]));

  layers.forEach((shape) => {
    const i = idxOf.get(shape.level);
    const zB = bottom.get(shape.level);
    (shape.connectors || []).forEach((cn) => {
      const p = resolveConnector(cn, settings);
      const dia = p.shape === "diamond";
      const peg = chamferedPrism(p.maleSize, p.maleHeight + EPS, p.chamfer, dia);
      peg.translate(cn.x, cn.y, zB - p.maleHeight);
      byId.get(shape.id).pegGeoms.push(peg);
      const mkHole = () => {
        const hg = chamferedPrism(p.femaleSize, p.femaleDepth + EPS, p.chamfer, dia);
        hg.translate(cn.x, cn.y, zB - p.femaleDepth); // top of level below == zB
        return hg;
      };
      if (i > 0) {
        (byLevel.get(present[i - 1]) || []).forEach((bs) => {
          if (bs.visible !== false && pointInPolygon(cn.x, cn.y, getWorldPoints(bs))) {
            (holesForShape[bs.id] ||= []).push(mkHole());
          }
        });
      } else if (hasBase) {
        baseHoles.push(mkHole());
      }
    });
  });

  const plates = entries.map(({ shape, zB, pegGeoms }) => {
    let geometry = plateGeometry(shape, zB);
    const holes = holesForShape[shape.id];
    if (holes && holes.length) geometry = subtractFrom(geometry, holes);
    return { id: shape.id, name: shape.name, color: levelColor(shape.level), level: shape.level, visible: shape.visible, geometry, pegGeoms };
  });

  return { plates, baseHoles, topZ };
}

export function buildPreview(layers, settings, base) {
  const baseTop = baseTopZ(base);
  const { plates, baseHoles, topZ } = buildAll(layers, settings, baseTop, !!base);
  const baseGeoms = base ? buildBaseFinal(base, baseHoles) : [];
  return { plates, baseGeoms, topZ };
}

export function buildExport(layers, settings, base) {
  const baseTop = baseTopZ(base);
  const { plates, baseHoles } = buildAll(layers, settings, baseTop, !!base);
  const solids = plates
    .filter((p) => p.visible !== false)
    .map((pl, idx) => {
      let geometry = pl.geometry;
      if (pl.pegGeoms.length) {
        try { geometry = csg(geometry, pl.pegGeoms, ADDITION); } catch (e) { /* keep plate only */ }
      }
      return { geometry, color: pl.color, name: pl.name || `shape-${idx + 1}` };
    });
  const baseGeoms = base ? buildBaseFinal(base, baseHoles) : [];
  return { solids, baseGeoms };
}
